import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useOrganizationStore, OrganizationMember, Organization } from '../store/organizationStore';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { usePageTitle } from '../hooks/usePageTitle';
import Select from '../components/Select';
import ConfirmationModal from '../components/ConfirmationModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

type OrgTab = 'overview' | 'people' | 'teams' | 'boards' | 'sso';

// ─── Org List ────────────────────────────────────────────────────────────────

const roleBadgeClass = (role?: string | null) => {
  switch (role) {
    case 'owner': return 'bg-orange-100 text-orange-700 dark:bg-orange-800 dark:text-orange-200';
    case 'admin': return 'bg-orange-100 text-orange-700';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
};

interface OrgListViewProps {
  organizations: Organization[];
  onSelect: (org: Organization) => void;
}

const OrgListView: React.FC<OrgListViewProps> = ({ organizations, onSelect }) => (
  <div className="space-y-3">
    {organizations.map(org => (
      <button
        key={org.id}
        onClick={() => onSelect(org)}
        className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:border-orange-300 dark:hover:border-orange-600 hover:shadow-md transition-all group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-orange-500 flex items-center justify-center text-white text-xl font-bold shrink-0">
              {org.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-gray-900 dark:text-white">{org.name}</span>
                {org.my_role && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleBadgeClass(org.my_role)}`}>
                    {org.my_role.charAt(0).toUpperCase() + org.my_role.slice(1)}
                  </span>
                )}
              </div>
              <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span>{org.member_count} member{org.member_count !== 1 ? 's' : ''}</span>
                <span>{org.team_count} team{org.team_count !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
          <svg className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
    ))}
  </div>
);

// ─── Member View (non-admin) ──────────────────────────────────────────────────

interface MemberViewProps {
  onBack: () => void;
}

const MemberView: React.FC<MemberViewProps> = ({ onBack }) => {
  const { currentOrganization, leaveOrganization, fetchCurrentOrganization } = useOrganizationStore();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [leaveModal, setLeaveModal] = useState(false);

  const handleLeave = async () => {
    if (!currentOrganization) return;
    try {
      await leaveOrganization(currentOrganization.id);
      showSuccess('Left Organization', `You have left ${currentOrganization.name}.`);
      await fetchCurrentOrganization();
      onBack();
    } catch (err) {
      showError('Error', err instanceof Error ? err.message : 'Failed to leave organization');
    }
    setLeaveModal(false);
  };

  if (!currentOrganization) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-lg bg-orange-500 flex items-center justify-center text-white text-2xl font-bold">
            {currentOrganization.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{currentOrganization.name}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span>{currentOrganization.members?.length ?? currentOrganization.member_count} members</span>
              <span>{currentOrganization.teams?.length ?? 0} teams</span>
              <span>{currentOrganization.boards?.length ?? 0} boards</span>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleBadgeClass(currentOrganization.my_role)}`}>
            {(currentOrganization.my_role ?? 'member').charAt(0).toUpperCase() + (currentOrganization.my_role ?? 'member').slice(1)}
          </span>
        </div>
      </div>

      {/* Members */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">Members</h3>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {currentOrganization.members?.map(member => (
            <div key={member.id} className="flex items-center gap-3 px-6 py-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                style={{ backgroundColor: member.user.avatar_color }}
              >
                {member.user.initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{member.user.display_name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{member.user_email || member.user.email}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleBadgeClass(member.role)}`}>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Leave */}
      <div className="flex justify-end">
        <button
          onClick={() => setLeaveModal(true)}
          className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          Leave Organization
        </button>
      </div>

      <ConfirmationModal
        isOpen={leaveModal}
        onClose={() => setLeaveModal(false)}
        onConfirm={handleLeave}
        title="Leave Organization"
        message={`Are you sure you want to leave ${currentOrganization.name}? You will lose access to all its boards and teams.`}
        confirmText="Leave"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

// ─── Admin View (owner / admin) ───────────────────────────────────────────────

interface AdminViewProps {
  onBack: () => void;
  activeTab: OrgTab;
  onTabChange: (tab: OrgTab) => void;
}

const AdminView: React.FC<AdminViewProps> = ({ onBack, activeTab, onTabChange }) => {
  const { user } = useAuthStore();
  const {
    currentOrganization,
    inviteMember,
    cancelInvite,
    resendInvite,
    removeMember,
    updateMemberRole: updateMemberRoleStore,
    transferOwnership,
    leaveOrganization,
    fetchCurrentOrganization,
    updateOrganizationName,
  } = useOrganizationStore();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const [peopleSubTab, setPeopleSubTab] = useState<'members' | 'invites'>('members');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [editRoleModal, setEditRoleModal] = useState<{ open: boolean; memberId: string; currentRole: string }>({ open: false, memberId: '', currentRole: '' });
  const [removeMemberModal, setRemoveMemberModal] = useState<{ open: boolean; memberId: string; memberName: string }>({ open: false, memberId: '', memberName: '' });
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editNameLoading, setEditNameLoading] = useState(false);
  const [transferOwnershipModal, setTransferOwnershipModal] = useState<{ open: boolean; memberId: string; memberName: string }>({ open: false, memberId: '', memberName: '' });
  const [leaveModal, setLeaveModal] = useState(false);
  const [ssoForm, setSsoForm] = useState({
    sso_domain: '',
    sso_enabled: false,
    entity_id: '',
    idp_metadata_url: '',
    idp_metadata_xml: '',
    attribute_mapping_email: 'email',
    attribute_mapping_first_name: 'first_name',
    attribute_mapping_last_name: 'last_name',
    sp_entity_id: '',
    sp_acs_url: '',
    sp_metadata_url: '',
    can_edit: true,
  });
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoMessage, setSsoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadSamlConfig = useCallback(async () => {
    if (!currentOrganization) return;
    const tokens = useAuthStore.getState().tokens;
    if (!tokens?.access) return;
    setSsoLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/saml/config/${currentOrganization.id}/`, {
        headers: { Authorization: `Bearer ${tokens.access}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSsoForm({
          sso_domain: data.sso_domain || '',
          sso_enabled: data.sso_enabled || false,
          entity_id: data.entity_id || '',
          idp_metadata_url: data.idp_metadata_url || '',
          idp_metadata_xml: data.idp_metadata_xml || '',
          attribute_mapping_email: data.attribute_mapping_email || 'email',
          attribute_mapping_first_name: data.attribute_mapping_first_name || 'first_name',
          attribute_mapping_last_name: data.attribute_mapping_last_name || 'last_name',
          sp_entity_id: data.sp_entity_id || data.sp_entity_id_effective || '',
          sp_acs_url: data.sp_acs_url || '',
          sp_metadata_url: data.sp_metadata_url || '',
          can_edit: data.can_edit !== false,
        });
      }
    } finally {
      setSsoLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (activeTab === 'sso') loadSamlConfig();
  }, [activeTab, loadSamlConfig]);

  const handleSaveSso = async () => {
    if (!currentOrganization) return;
    const tokens = useAuthStore.getState().tokens;
    if (!tokens?.access) return;
    setSsoSaving(true);
    setSsoMessage(null);
    try {
      const {
        sp_entity_id: _spEntityId,
        sp_acs_url: _spAcsUrl,
        sp_metadata_url: _spMetadataUrl,
        can_edit: _canEdit,
        ...payload
      } = ssoForm;
      const res = await fetch(`${API_BASE_URL}/auth/saml/config/${currentOrganization.id}/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${tokens.access}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setSsoForm(f => ({
          ...f,
          entity_id: data.entity_id ?? '',
          sp_entity_id: data.sp_entity_id || f.sp_entity_id,
          sp_acs_url: data.sp_acs_url || f.sp_acs_url,
          sp_metadata_url: data.sp_metadata_url || f.sp_metadata_url,
        }));
        setSsoMessage({ type: 'success', text: 'SSO configuration saved.' });
      } else {
        setSsoMessage({ type: 'error', text: data.error || 'Failed to save SSO configuration.' });
      }
    } catch {
      setSsoMessage({ type: 'error', text: 'Failed to save SSO configuration.' });
    } finally {
      setSsoSaving(false);
    }
  };

  const allMembers = useMemo(() => {
    if (!currentOrganization) return [];
    const ownerTeams = currentOrganization.teams?.filter((team: any) =>
      team.created_by?.id === currentOrganization.owner.id ||
      team.members?.some((m: any) => m.id === currentOrganization.owner.id)
    ).map((team: any) => ({ id: team.id, name: team.name })) || [];

    const ownerAsMember: OrganizationMember = {
      id: currentOrganization.owner.id,
      user: currentOrganization.owner,
      user_email: currentOrganization.owner.email || '',
      user_id_short: currentOrganization.owner.id.substring(0, 12).toUpperCase(),
      teams: ownerTeams,
      role: 'owner' as const,
      joined_at: currentOrganization.created_at,
    };
    const nonOwnerMembers = (currentOrganization.members || []).filter(
      m => m.user.id !== currentOrganization.owner.id
    );
    return [ownerAsMember, ...nonOwnerMembers];
  }, [currentOrganization]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery) return allMembers;
    const q = searchQuery.toLowerCase();
    return allMembers.filter(m =>
      m.user.display_name?.toLowerCase().includes(q) ||
      m.user_email?.toLowerCase().includes(q) ||
      m.user.email?.toLowerCase().includes(q)
    );
  }, [allMembers, searchQuery]);

  const filteredInvites = useMemo(() => {
    if (!currentOrganization?.invites) return [];
    if (!searchQuery) return currentOrganization.invites;
    const q = searchQuery.toLowerCase();
    return currentOrganization.invites.filter(inv => inv.email.toLowerCase().includes(q));
  }, [currentOrganization?.invites, searchQuery]);

  if (!currentOrganization) return null;

  const isOwner = currentOrganization.my_role === 'owner';
  const memberCount = allMembers.length;
  const memberLimit = currentOrganization?.subscription?.plan?.max_users;
  const canAddMembers = memberLimit === null || memberCount < memberLimit;

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-orange-100 text-orange-700 dark:bg-orange-800 dark:text-orange-200';
      case 'admin': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const formatRelativeTime = (dateString: string) => {
    const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
    if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
    return `${Math.floor(diff / 31536000)}y ago`;
  };

  const handleInvite = async () => {
    if (!inviteEmail || !currentOrganization) return;
    try {
      await inviteMember(currentOrganization.id, inviteEmail, inviteRole);
      showSuccess('Invite Sent', `Invitation sent to ${inviteEmail}`);
      setInviteModalOpen(false);
      setInviteEmail('');
      setInviteRole('member');
    } catch (error) {
      showError('Invite Failed', error instanceof Error ? error.message : 'Failed to send invitation');
    }
  };

  const handleRemoveMember = async () => {
    if (!removeMemberModal.memberId || !currentOrganization) return;
    try {
      await removeMember(currentOrganization.id, removeMemberModal.memberId);
      showSuccess('Member Removed', 'Member has been removed from organization');
      setRemoveMemberModal({ open: false, memberId: '', memberName: '' });
    } catch (error) {
      showError('Remove Failed', error instanceof Error ? error.message : 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (newRole: 'owner' | 'admin' | 'member') => {
    if (!editRoleModal.memberId || !currentOrganization) return;
    try {
      await updateMemberRoleStore(currentOrganization.id, editRoleModal.memberId, newRole);
      showSuccess('Role Updated', 'Member role has been updated');
      setEditRoleModal({ open: false, memberId: '', currentRole: '' });
    } catch (error) {
      showError('Update Failed', error instanceof Error ? error.message : 'Failed to update role');
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferOwnershipModal.memberId || !currentOrganization) return;
    try {
      await transferOwnership(currentOrganization.id, transferOwnershipModal.memberId);
      showSuccess('Ownership Transferred', 'Organization ownership has been transferred successfully');
      setTransferOwnershipModal({ open: false, memberId: '', memberName: '' });
    } catch (error) {
      showError('Transfer Failed', error instanceof Error ? error.message : 'Failed to transfer ownership');
    }
  };

  const handleLeave = async () => {
    if (!currentOrganization) return;
    try {
      await leaveOrganization(currentOrganization.id);
      showSuccess('Left Organization', `You have left ${currentOrganization.name}.`);
      await fetchCurrentOrganization();
      onBack();
    } catch (err) {
      showError('Error', err instanceof Error ? err.message : 'Failed to leave organization');
    }
    setLeaveModal(false);
  };

  const handleUpdateName = async () => {
    if (!currentOrganization || !editNameValue.trim()) return;
    setEditNameLoading(true);
    try {
      await updateOrganizationName(currentOrganization.id, editNameValue.trim());
      showSuccess('Name Updated', 'Organization name has been updated.');
      setEditNameOpen(false);
    } catch (err) {
      showError('Update Failed', err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setEditNameLoading(false);
    }
  };

  const toggleMemberSelection = (id: string) => {
    setSelectedMembers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedMembers(
      selectedMembers.size === filteredMembers.length
        ? new Set()
        : new Set(filteredMembers.map(m => m.id))
    );
  };

  return (
    <>
      {/* Org Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-lg bg-orange-500 flex items-center justify-center text-white text-2xl font-bold shrink-0">
            {currentOrganization.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            {editNameOpen ? (
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="text"
                  value={editNameValue}
                  onChange={e => setEditNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleUpdateName(); if (e.key === 'Escape') setEditNameOpen(false); }}
                  autoFocus
                  className="flex-1 px-2 py-1 text-lg font-semibold border border-orange-400 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleUpdateName}
                  disabled={editNameLoading || !editNameValue.trim()}
                  className="px-3 py-1 text-xs font-medium bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 transition-colors"
                >
                  {editNameLoading ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => setEditNameOpen(false)}
                  className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-1 group">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{currentOrganization.name}</h2>
                <button
                  onClick={() => { setEditNameValue(currentOrganization.name); setEditNameOpen(true); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-orange-500 transition-all rounded"
                  title="Edit name"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">ID: {currentOrganization.id}</p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span>Created: {formatDate(currentOrganization.created_at)}</span>
              {currentOrganization.subscription && (
                <span>Plan: {currentOrganization.subscription.plan?.name || 'N/A'}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px overflow-x-auto">
            {(['overview', 'people', 'teams', 'boards'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-orange-500 text-orange-500 dark:text-orange-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab === 'overview' && 'Overview'}
                {tab === 'people' && `People (${currentOrganization.members?.length || 0})`}
                {tab === 'teams' && `Teams (${currentOrganization.teams?.length || 0})`}
                {tab === 'boards' && `Boards (${currentOrganization.boards?.length || 0})`}
              </button>
            ))}
            {isOwner && (
              <button
                onClick={() => onTabChange('sso')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'sso'
                    ? 'border-orange-500 text-orange-500 dark:text-orange-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                SSO / SAML2
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'People', value: currentOrganization.members?.length || 0 },
                  { label: 'Teams', value: currentOrganization.teams?.length || 0 },
                  { label: 'Boards', value: currentOrganization.boards?.length || 0 },
                ].map(stat => (
                  <div key={stat.label} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Subscription</h3>
                  <Link
                    to="/app/subscription"
                    className="px-3 py-1.5 text-xs font-medium text-orange-500 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-800/20 rounded-lg transition-colors border border-orange-200 dark:border-orange-700"
                  >
                    {currentOrganization.subscription ? 'Manage Subscription' : 'Setup Subscription'}
                  </Link>
                </div>
                {currentOrganization.subscription ? (
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center justify-between">
                      <span>Plan:</span>
                      <span className="font-medium text-gray-900 dark:text-white capitalize">
                        {currentOrganization.subscription.plan?.display_name || currentOrganization.subscription.plan?.name || 'N/A'}
                        {currentOrganization.subscription.status === 'trial' && currentOrganization.subscription.is_trial_active && (
                          <span className="ml-2 px-2 py-0.5 bg-orange-100 dark:bg-orange-800 text-orange-600 dark:text-orange-300 text-xs font-semibold rounded-full">Trial</span>
                        )}
                      </span>
                    </div>
                    {currentOrganization.subscription.plan?.price_per_user_monthly !== undefined && (
                      <div className="flex items-center justify-between">
                        <span>Price:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {Number(currentOrganization.subscription.plan.price_per_user_monthly) > 0
                            ? `$${Number(currentOrganization.subscription.plan.price_per_user_monthly).toFixed(2)}/user/month`
                            : 'Free'}
                        </span>
                      </div>
                    )}
                    {currentOrganization.subscription.status === 'trial' && currentOrganization.subscription.trial_end_date && (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                        <span>Trial ends:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {new Date(currentOrganization.subscription.trial_end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">No subscription found.</p>
                )}
              </div>

              {/* Leave (admin who is not owner can leave) */}
              {!isOwner && (
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setLeaveModal(true)}
                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    Leave Organization
                  </button>
                </div>
              )}
            </div>
          )}

          {/* People */}
          {activeTab === 'people' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">People</h3>
                  {memberLimit !== null && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{memberCount}/{memberLimit} members</span>
                  )}
                </div>
                <button
                  onClick={() => setInviteModalOpen(true)}
                  disabled={!canAddMembers}
                  className="px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Invite Member
                </button>
              </div>

              <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 mb-3">
                {(['members', 'invites'] as const).map(sub => (
                  <button
                    key={sub}
                    onClick={() => setPeopleSubTab(sub)}
                    className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                      peopleSubTab === sub
                        ? 'border-orange-500 text-orange-500 dark:text-orange-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {sub === 'members' ? `Members (${allMembers.length})` : `Invites (${currentOrganization?.invites?.length || 0})`}
                  </button>
                ))}
              </div>

              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {peopleSubTab === 'members' && selectedMembers.size > 0 && (
                <div className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-800/20 rounded mb-3">
                  <span className="text-xs text-gray-700 dark:text-gray-300">{selectedMembers.size} selected</span>
                  <button onClick={() => setSelectedMembers(new Set())} className="text-xs text-orange-500 dark:text-orange-400 hover:underline">Clear</button>
                </div>
              )}

              {peopleSubTab === 'members' && (
                <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">
                          <input
                            type="checkbox"
                            checked={selectedMembers.size === filteredMembers.length && filteredMembers.length > 0}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                          />
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Role</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 hidden md:table-cell">Teams</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 hidden lg:table-cell">Joined</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredMembers.map(member => (
                        <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedMembers.has(member.id)}
                              onChange={() => toggleMemberSelection(member.id)}
                              className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                              {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: member.user.avatar_color }}>
                                {member.user.initials}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {member.user.display_name}
                                {member.user.id === user?.id && <span className="ml-1 text-xs text-gray-500">(You)</span>}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-xs text-gray-600 dark:text-gray-400">{member.user_email || member.user.email || '-'}</span>
                          </td>
                          <td className="px-3 py-2 hidden md:table-cell">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {member.teams && member.teams.length > 0
                                ? `${member.teams.slice(0, 2).map(t => t.name).join(', ')}${member.teams.length > 2 ? ` +${member.teams.length - 2}` : ''}`
                                : '-'}
                            </span>
                          </td>
                          <td className="px-3 py-2 hidden lg:table-cell">
                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatRelativeTime(member.joined_at)}</span>
                          </td>
                          <td className="px-3 py-2">
                            {member.role !== 'owner' ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditRoleModal({ open: true, memberId: member.user.id, currentRole: member.role })}
                                  className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  Edit Role
                                </button>
                                <button
                                  onClick={() => setRemoveMemberModal({ open: true, memberId: member.user.id, memberName: member.user.display_name })}
                                  className="px-2 py-1 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                >
                                  Remove
                                </button>
                                {isOwner && (
                                  <button
                                    onClick={() => setTransferOwnershipModal({ open: true, memberId: member.user.id, memberName: member.user.display_name })}
                                    className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors hidden xl:block"
                                  >
                                    Transfer
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredMembers.length === 0 && (
                    <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      {searchQuery ? 'No members found matching your search.' : 'No members found.'}
                    </div>
                  )}
                </div>
              )}

              {peopleSubTab === 'invites' && (
                <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Role</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Invited By</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 hidden lg:table-cell">Expires</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredInvites.map(invite => (
                        <tr key={invite.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-3 py-2"><span className="text-sm text-gray-900 dark:text-white">{invite.email}</span></td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(invite.role)}`}>
                              {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: invite.invited_by.avatar_color }}>
                                {invite.invited_by.initials}
                              </div>
                              <span className="text-xs text-gray-600 dark:text-gray-400">{invite.invited_by.display_name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 hidden lg:table-cell">
                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatRelativeTime(invite.expires_at)}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={async () => {
                                  try { await resendInvite(currentOrganization.id, invite.id); showSuccess('Invite Resent', `Resent to ${invite.email}`); }
                                  catch (e) { showError('Resend Failed', e instanceof Error ? e.message : 'Failed'); }
                                }}
                                className="px-2 py-1 text-xs text-orange-500 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-800/20 rounded"
                              >
                                Resend
                              </button>
                              <button
                                onClick={async () => {
                                  try { await cancelInvite(currentOrganization.id, invite.id); showSuccess('Invite Cancelled', 'Invitation cancelled'); }
                                  catch (e) { showError('Cancel Failed', e instanceof Error ? e.message : 'Failed'); }
                                }}
                                className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredInvites.length === 0 && (
                    <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      {searchQuery ? 'No invites found.' : 'No pending invites.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Teams */}
          {activeTab === 'teams' && (
            <div className="space-y-3">
              {currentOrganization.teams?.map((team: any) => (
                <div key={team.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">{team.name}</h3>
                      {team.description && <p className="text-sm text-gray-600 dark:text-gray-400">{team.description}</p>}
                    </div>
                    <Link to="/app/teams" className="text-sm text-orange-500 dark:text-orange-400 hover:underline">View →</Link>
                  </div>
                </div>
              ))}
              {(!currentOrganization.teams || currentOrganization.teams.length === 0) && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">No teams found</p>
              )}
            </div>
          )}

          {/* SSO / SAML2 */}
          {activeTab === 'sso' && (
            <div className="space-y-5">
              {currentOrganization.subscription?.plan?.name !== 'business' && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-sm text-amber-800 dark:text-amber-300">
                  SAML2 SSO is only available on the <strong>Business plan</strong>. Your current plan is <strong className="capitalize">{currentOrganization.subscription?.plan?.display_name || currentOrganization.subscription?.plan?.name || 'Free'}</strong>.{' '}
                  <Link to="/app/subscription" className="underline hover:no-underline">Upgrade</Link>
                </div>
              )}

              {ssoLoading ? (
                <div className="flex justify-center py-8"><LoadingSpinner /></div>
              ) : (
                <div className="space-y-5">
                  {ssoForm.sp_acs_url && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200">SP details to register in your corporate IdP</p>
                      <p className="text-xs text-blue-800 dark:text-blue-300">
                        In Okta, Microsoft Entra ID (Azure AD), Google Workspace, or another SAML 2.0 IdP admin panel,
                        enter the values below <strong>exactly</strong>. You can leave the SP Entity ID field empty.
                      </p>
                      {[
                        { label: 'ACS URL (Assertion Consumer Service)', value: ssoForm.sp_acs_url },
                        { label: 'Entity ID / Audience', value: ssoForm.sp_entity_id },
                        { label: 'Metadata URL (optional)', value: ssoForm.sp_metadata_url },
                      ].map(({ label, value }) => value ? (
                        <div key={label}>
                          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">{label}</label>
                          <div className="flex gap-2">
                            <input readOnly value={value} className="flex-1 px-2 py-1.5 text-xs font-mono border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                            <button type="button" onClick={() => navigator.clipboard.writeText(value)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 shrink-0">Copy</button>
                          </div>
                        </div>
                      ) : null)}
                      {process.env.NODE_ENV === 'development' && (
                        <details className="text-xs text-blue-800 dark:text-blue-400">
                          <summary className="cursor-pointer font-medium">Local test (samltest.id) — do not use in production</summary>
                          <p className="mt-2">
                            IdP metadata URL in development:{' '}
                            <code className="font-mono">https://idp.samltest.id/idp/shibboleth</code>
                            {' · '}Example email claim:{' '}
                            <code className="font-mono">urn:oid:0.9.2342.19200300.100.1.3</code>
                          </p>
                        </details>
                      )}
                    </div>
                  )}

                  {/* Enable toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Enable SSO</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Users on the configured domain will be prompted to sign in via SSO</p>
                    </div>
                    <button
                      onClick={() => setSsoForm(f => ({ ...f, sso_enabled: !f.sso_enabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${ssoForm.sso_enabled ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${ssoForm.sso_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {/* SSO Domain */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SSO Domain</label>
                    <input
                      type="text"
                      placeholder="e.g. acme.com"
                      value={ssoForm.sso_domain}
                      onChange={e => setSsoForm(f => ({ ...f, sso_domain: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Users signing in with this email domain will be redirected to your IdP.</p>
                  </div>

                  <details className="text-sm">
                    <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-300">
                      Advanced: custom Entity ID (optional)
                    </summary>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Leave empty to use the Entity ID from the blue box above. Do not paste samltest or IdP application URLs here.
                    </p>
                    <input
                      type="text"
                      placeholder="Leave empty — default Retrosive metadata URL is used"
                      value={ssoForm.entity_id}
                      onChange={e => setSsoForm(f => ({ ...f, entity_id: e.target.value }))}
                      className="mt-2 w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
                    />
                  </details>

                  {/* IdP Metadata URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IdP Metadata URL</label>
                    <input
                      type="url"
                      placeholder="https://login.microsoftonline.com/.../federationmetadata/2007-06/federationmetadata.xml"
                      value={ssoForm.idp_metadata_url}
                      onChange={e => setSsoForm(f => ({ ...f, idp_metadata_url: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  {/* IdP Metadata XML */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IdP Metadata XML <span className="font-normal text-gray-400">(alternative to URL)</span></label>
                    <textarea
                      rows={5}
                      placeholder="Paste XML metadata here..."
                      value={ssoForm.idp_metadata_xml}
                      onChange={e => setSsoForm(f => ({ ...f, idp_metadata_xml: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
                    />
                  </div>

                  {/* Attribute Mapping */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attribute Mapping</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Enter your IdP&apos;s SAML claim / attribute names (from Okta, Entra ID, or Google Workspace documentation).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {([
                        { key: 'attribute_mapping_email', label: 'Email attribute' },
                        { key: 'attribute_mapping_first_name', label: 'First name attribute' },
                        { key: 'attribute_mapping_last_name', label: 'Last name attribute' },
                      ] as const).map(({ key, label }) => (
                        <div key={key}>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</label>
                          <input
                            type="text"
                            value={ssoForm[key]}
                            onChange={e => setSsoForm(f => ({ ...f, [key]: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {ssoMessage && (
                    <div className={`p-3 rounded-lg text-sm ${ssoMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'}`}>
                      {ssoMessage.text}
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveSso}
                      disabled={ssoSaving}
                      className="px-4 py-2 text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {ssoSaving ? 'Saving...' : 'Save SSO Configuration'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Boards */}
          {activeTab === 'boards' && (
            <div className="space-y-3">
              {currentOrganization.boards?.map((board: any) => (
                <div key={board.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">{board.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          board.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : board.status === 'locked' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                        }`}>{board.status}</span>
                      </div>
                      {board.description && <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{board.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        {board.team_name && <span>Team: {board.team_name}</span>}
                        <span>Created: {new Date(board.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      </div>
                    </div>
                    <Link to={`/app/board/${board.id}`} className="ml-4 text-sm text-orange-500 dark:text-orange-400 hover:underline whitespace-nowrap">Open →</Link>
                  </div>
                </div>
              ))}
              {(!currentOrganization.boards || currentOrganization.boards.length === 0) && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">No boards found</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {inviteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Invite Member</h3>
              <button onClick={() => { setInviteModalOpen(false); setInviteEmail(''); setInviteRole('member'); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                <Select
                  value={inviteRole}
                  onChange={v => setInviteRole(v as 'admin' | 'member')}
                  options={[{ value: 'member', label: 'Member' }, { value: 'admin', label: 'Admin' }]}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setInviteModalOpen(false); setInviteEmail(''); setInviteRole('member'); }} className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Cancel</button>
                <button onClick={handleInvite} disabled={!inviteEmail} className="px-3 py-2 text-xs font-medium bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed">Send Invite</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {editRoleModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Edit Role</h3>
              <button onClick={() => setEditRoleModal({ open: false, memberId: '', currentRole: '' })} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Select a new role:</p>
            <div className="space-y-2">
              {['admin', 'member'].map(role => (
                <button
                  key={role}
                  onClick={() => handleUpdateRole(role as 'admin' | 'member')}
                  className={`w-full px-3 py-2 text-sm text-left rounded ${
                    editRoleModal.currentRole === role
                      ? 'bg-orange-100 dark:bg-orange-800 text-orange-600 dark:text-orange-300'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setEditRoleModal({ open: false, memberId: '', currentRole: '' })} className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={removeMemberModal.open}
        onClose={() => setRemoveMemberModal({ open: false, memberId: '', memberName: '' })}
        onConfirm={handleRemoveMember}
        title="Remove Member"
        message={`Are you sure you want to remove ${removeMemberModal.memberName} from this organization?`}
        confirmText="Remove" cancelText="Cancel" type="danger"
      />

      <ConfirmationModal
        isOpen={transferOwnershipModal.open}
        onClose={() => setTransferOwnershipModal({ open: false, memberId: '', memberName: '' })}
        onConfirm={handleTransferOwnership}
        title="Transfer Ownership"
        message={`Transfer ownership to ${transferOwnershipModal.memberName}? You will become an admin.`}
        confirmText="Transfer" cancelText="Cancel" type="warning"
      />

      <ConfirmationModal
        isOpen={leaveModal}
        onClose={() => setLeaveModal(false)}
        onConfirm={handleLeave}
        title="Leave Organization"
        message={`Are you sure you want to leave ${currentOrganization.name}?`}
        confirmText="Leave" cancelText="Cancel" type="danger"
      />
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const VALID_TABS: OrgTab[] = ['overview', 'people', 'teams', 'boards', 'sso'];

const OrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgId, tab } = useParams<{ orgId?: string; tab?: string }>();
  const { user } = useAuthStore();
  const {
    organizations,
    currentOrganization,
    fetchOrganizations,
    fetchCurrentOrganization,
    loading,
    error,
  } = useOrganizationStore();

  usePageTitle(currentOrganization ? currentOrganization.name : 'Organizations');

  const activeTab: OrgTab = VALID_TABS.includes(tab as OrgTab) ? (tab as OrgTab) : 'overview';

  const fetchedListRef = useRef(false);

  // Always load the org list
  useEffect(() => {
    if (!user || fetchedListRef.current) return;
    fetchedListRef.current = true;
    fetchOrganizations();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When orgId is in the URL, always load fresh org detail (handles page refresh too)
  useEffect(() => {
    if (!orgId) return;
    fetchCurrentOrganization(orgId);
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh org detail when tab regains focus (picks up accepted invites / new members)
  useEffect(() => {
    if (!orgId) return;
    const onFocus = () => fetchCurrentOrganization(orgId);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 30s while on People tab so invites update without a manual refresh
  useEffect(() => {
    if (!orgId || activeTab !== 'people') return;
    const id = setInterval(() => fetchCurrentOrganization(orgId), 30_000);
    return () => clearInterval(id);
  }, [orgId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectOrg = (org: Organization) => {
    navigate(`/app/organization/${org.id}`);
  };

  const handleBack = () => {
    navigate('/app/organization');
    fetchOrganizations();
  };

  const handleTabChange = (newTab: OrgTab) => {
    if (!orgId) return;
    if (newTab === 'overview') {
      navigate(`/app/organization/${orgId}`, { replace: true });
    } else {
      navigate(`/app/organization/${orgId}/${newTab}`, { replace: true });
    }
  };

  const isAdmin = currentOrganization?.my_role === 'owner' || currentOrganization?.my_role === 'admin';

  // List view
  if (!orgId) {
    if (loading && organizations.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-6">
            <Link to="/app/dashboard" className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Organizations</h1>
            {organizations.length > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                You are a member of {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {organizations.length === 0 && !loading ? (
            <div className="text-center py-16">
              <p className="text-gray-500 dark:text-gray-400 mb-4">You are not a member of any organization yet.</p>
              <button onClick={() => navigate('/app/dashboard')} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors">
                Back to Dashboard
              </button>
            </div>
          ) : (
            <OrgListView organizations={organizations} onSelect={handleSelectOrg} />
          )}
        </div>
      </div>
    );
  }

  // Detail view
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All Organizations
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {currentOrganization?.name ?? ''}
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : currentOrganization ? (
          isAdmin
            ? <AdminView onBack={handleBack} activeTab={activeTab} onTabChange={handleTabChange} />
            : <MemberView onBack={handleBack} />
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 dark:text-gray-400 mb-4">Organization not found.</p>
            <button onClick={handleBack} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors">
              Back to Organizations
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationPage;
